/**
 * Coded user-facing refusals: a server refusal whose wire text is
 * `[code] German reason`, meant to be shown to the user verbatim. This module
 * is the single owner of the allowlist — only the codes below are displayed;
 * any other `[x] …` text is technical and the client shows its own fallback.
 * Backends format their refusals with {@link formatUserFacingRefusal}; the
 * client display rule decodes them with {@link decodeUserFacingRefusal}.
 *
 * @since 2026-10-24
 */

/**
 * The allowlisted codes of user-facing refusals.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * const code = USER_FACING_REFUSAL_CODES.swsImportReleasePending;
 * ```
 */
export const USER_FACING_REFUSAL_CODES = {
  /** SWS import execution while the release gate is closed. */
  swsImportReleasePending: "sws-import-release-acceptance-pending",
  /** SWS delivery whose X005 does not match the receiving practice. */
  swsImportPracticeMismatch: "sws-import-practice-mismatch",
  /** SWS import started while another import of the job is running (Step 15 lane). */
  swsImportAlreadyRunning: "sws-import-already-running",
  /** Practice-data import conflicts with existing data (names table and row; Step 15 lane). */
  fhirPortabilityImportConflict: "fhir-portability-import-conflict",
  /** A practice-data import is already running for this practice (Step 15 lane). */
  fhirPortabilityImportRunning: "fhir-portability-import-running",
  /** AzP delivery is encrypted; a single-patient delivery needs its PIN. */
  azpImportPinRequired: "azp-import-pin-required",
  /** AzP delivery PIN is wrong (or the delivery is damaged). */
  azpImportWrongPin: "azp-import-wrong-pin",
  /** A PIN was given for an AzP delivery that is not PIN-protected. */
  azpImportNotPinProtected: "azp-import-not-pin-protected",
  /** AzP delivery without an AZP_DATA_*.xml file. */
  azpImportDataMissing: "azp-import-data-missing",
  /** AzP data that do not satisfy the AzP XML schema (detail only in server logs). */
  azpImportInvalidData: "azp-import-invalid-data",
  /** PIN-protected AzP delivery whose ExportArt is not 1. */
  azpImportExportArtMismatch: "azp-import-export-art-mismatch",
  /** AzP archive that cannot be unpacked (detail only in server logs). */
  azpImportArchiveUnreadable: "azp-import-archive-unreadable",
  /** ÜBAG own-lab costs (603) for a Praxislabor that is not part of the BAG (D68, Step 14). */
  eigenlaborOwnLabNotInBag: "eigenlabor-own-lab-not-in-bag",
  /** ÜBAG own-lab BEL price outside the case KZV's ceiling: contract question (D68, Step 14). */
  eigenlaborPriceContractQuestion: "eigenlabor-price-contract-question",
  /** BEL price context of the case (Leistungsbereich, Kassengruppe) incomplete: no ceiling can be chosen (D72 V1, Step 14). */
  eigenlaborPriceContextIncomplete: "eigenlabor-price-context-incomplete",
  /** ÜBAG own-lab BEL price above the applicable ceiling, or no ceiling hinterlegt (D68, Step 14). */
  eigenlaborPriceAboveCeiling: "eigenlabor-price-above-ceiling",
  /** Lab billing context (case, Encounter, Account, first-treatment site) not resolvable (D72 V3, Step 14). */
  labBillingContextUnresolved: "lab-billing-context-unresolved",
  /** Lab order without exactly one CarePlan: its costs cannot enter KZBV billing (Step 14). */
  labOrderCaseLinkMissing: "lab-order-case-link-missing",
} as const;

/**
 * One allowlisted user-facing refusal code.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * const code: UserFacingRefusalCode = "azp-import-wrong-pin";
 * ```
 */
export type UserFacingRefusalCode =
  (typeof USER_FACING_REFUSAL_CODES)[keyof typeof USER_FACING_REFUSAL_CODES];

const ALLOWLIST: ReadonlySet<string> = new Set(
  Object.values(USER_FACING_REFUSAL_CODES),
);
const CODED_TEXT = /^\[([a-z0-9]+(?:-[a-z0-9]+)*)\] (\S[\s\S]*)$/u;

/**
 * Format a user-facing refusal for the wire: `[code] reason`.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * formatUserFacingRefusal(USER_FACING_REFUSAL_CODES.azpImportWrongPin, "Die PIN ist falsch");
 * // "[azp-import-wrong-pin] Die PIN ist falsch"
 * ```
 */
export function formatUserFacingRefusal(
  code: UserFacingRefusalCode,
  reason: string,
): string {
  return `[${code}] ${reason}`;
}

/**
 * Decode a wire text as an allowlisted user-facing refusal; `undefined` for
 * any other text, including unknown codes.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * decodeUserFacingRefusal("[azp-import-wrong-pin] Die PIN ist falsch");
 * // { code: "azp-import-wrong-pin", reason: "Die PIN ist falsch", text: "[azp-import-wrong-pin] Die PIN ist falsch" }
 * ```
 */
export function decodeUserFacingRefusal(text: unknown):
  | {
      readonly code: string;
      readonly reason: string;
      readonly text: string;
    }
  | undefined {
  if (typeof text !== "string") return undefined;
  const trimmed = text.trim();
  const match = CODED_TEXT.exec(trimmed);
  if (match === null) return undefined;
  const [, code, reason] = match;
  if (code === undefined || reason === undefined || !ALLOWLIST.has(code)) {
    return undefined;
  }
  return { code, reason, text: trimmed };
}

/**
 * The German text to show for a wire text: the reason of an allowlisted coded
 * refusal (without its `[code]`, which stays for diagnostics and tests),
 * otherwise `fallback`.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * displayUserFacingRefusal("[azp-import-wrong-pin] Die PIN ist falsch", "Fehler");
 * // "Die PIN ist falsch"
 * ```
 */
export function displayUserFacingRefusal(text: unknown, fallback: string): string {
  return decodeUserFacingRefusal(text)?.reason ?? fallback;
}
